/**
 * Layout algoritmy (Epic 7). Dvě nezávislé strategie podle epic-7 kap. 8:
 *
 * - `computeFullAutoLayout` — plný přepočet layoutu celého schématu (E7-US1/US2),
 *   layered algoritmus přes elkjs (`elk.bundled.js` — vlastní web worker
 *   zabalený uvnitř bundlu, žádný samostatný asset navíc pro Vite).
 * - `computeIncrementalPlacement` — lehký lokální výpočet BEZ elkjs pro
 *   umístění jedné nové tabulky (E7-US3/US3b) — kandidátské pozice okolo
 *   propojených tabulek ohodnocené vzdáleností + počtem nově vzniklých
 *   křížení, bez plného přepočtu zbytku schématu.
 *
 * Skupiny jsou při plném layoutu atomická jednotka (kap. 2 rozhodnutí:
 * "zachová relativní pozice tabulek uvnitř skupiny") — NE přes ELK
 * compound/hierarchické uzly (ty by ELK daly volnost přeuspořádat i VNITŘEK
 * skupiny), ale přes super-uzel s pevnými rozměry = aktuální bounding box
 * (`computeGroupBoundingBox`) a rigidní posun všech členů o deltu mezi starou
 * a novou pozicí super-uzlu po layoutu — stejný princip jako
 * `CanvasView.tsx`'s `handleNodeDragStop` `groupFrame` větev, jen programově.
 */
import ELK, { type ElkExtendedEdge, type ElkNode } from 'elkjs/lib/elk.bundled.js';
import type { Group, SchemaModel } from '@bits2schema/shared';
import { resolveForeignKeyEdges } from '@bits2schema/shared';
import { TABLE_NODE_FALLBACK_HEIGHT, TABLE_NODE_WIDTH, computeGroupBoundingBox, type BoundingBox, type BoxSize } from './geometry';

export interface Point {
  x: number;
  y: number;
}

const elk = new ELK();

const FULL_LAYOUT_OPTIONS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.spacing.nodeNode': '60',
  'elk.layered.spacing.nodeNodeBetweenLayers': '90',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
};

export function isTableConnected(schema: SchemaModel, tableId: string): boolean {
  return resolveForeignKeyEdges(schema).some(
    (edge) => edge.sourceTableId === tableId || edge.targetTableId === tableId,
  );
}

/**
 * Plný auto-layout nad celým schématem. `effectivePositions` musí obsahovat
 * pozici KAŽDÉ tabulky ze `schema.tables` (volající dodá stejnou "efektivní"
 * pozici — uložená, nebo render-only fallback grid — jakou dnes používá
 * `CanvasView.tsx#effectiveTablePositions`), protože se z ní počítá aktuální
 * bounding box skupin. Vrací kompletní novou mapu pozic pro každou tabulku.
 */
export async function computeFullAutoLayout(
  schema: SchemaModel,
  groups: Group[],
  effectivePositions: Record<string, Point>,
  tableSizes: Record<string, BoxSize>,
): Promise<Record<string, Point>> {
  const groupIdByTable = new Map<string, string>();
  for (const group of groups) {
    for (const tableId of group.memberTableIds) groupIdByTable.set(tableId, group.id);
  }

  const groupBoxes = new Map<string, BoundingBox>();
  for (const group of groups) {
    const box = computeGroupBoundingBox(group.memberTableIds, effectivePositions, tableSizes);
    if (box) groupBoxes.set(group.id, box);
  }

  const layoutIdForTable = (tableId: string): string => {
    const groupId = groupIdByTable.get(tableId);
    return groupId && groupBoxes.has(groupId) ? groupId : tableId;
  };

  const children: ElkNode[] = [];
  const seenLayoutIds = new Set<string>();
  for (const table of schema.tables) {
    const layoutId = layoutIdForTable(table.id);
    if (seenLayoutIds.has(layoutId)) continue;
    seenLayoutIds.add(layoutId);
    if (layoutId === table.id) {
      const size = tableSizes[table.id] ?? { width: TABLE_NODE_WIDTH, height: TABLE_NODE_FALLBACK_HEIGHT };
      children.push({ id: table.id, width: size.width, height: size.height });
    } else {
      const box = groupBoxes.get(layoutId);
      if (box) children.push({ id: layoutId, width: box.width, height: box.height });
    }
  }

  const edgeMap = new Map<string, ElkExtendedEdge>();
  for (const resolved of resolveForeignKeyEdges(schema)) {
    if (!resolved.targetTableId) continue;
    const sourceLayoutId = layoutIdForTable(resolved.sourceTableId);
    const targetLayoutId = layoutIdForTable(resolved.targetTableId);
    // Self-FK i FK mezi dvěma tabulkami TÉŽE skupiny jsou pro layout mezi
    // super-uzly irelevantní (oba konce by vyšly na stejné layout id).
    if (sourceLayoutId === targetLayoutId) continue;
    edgeMap.set(resolved.foreignKey.id, {
      id: resolved.foreignKey.id,
      sources: [sourceLayoutId],
      targets: [targetLayoutId],
    });
  }

  const graph: ElkNode = {
    id: 'root',
    layoutOptions: FULL_LAYOUT_OPTIONS,
    children,
    edges: [...edgeMap.values()],
  };

  const result = await elk.layout(graph);

  const positions: Record<string, Point> = {};
  for (const node of result.children ?? []) {
    const x = node.x ?? 0;
    const y = node.y ?? 0;
    const box = groupBoxes.get(node.id);
    if (box) {
      const dx = x - box.x;
      const dy = y - box.y;
      const group = groups.find((candidate) => candidate.id === node.id);
      for (const tableId of group?.memberTableIds ?? []) {
        const original = effectivePositions[tableId];
        if (original) positions[tableId] = { x: original.x + dx, y: original.y + dy };
      }
    } else {
      positions[node.id] = { x, y };
    }
  }

  return positions;
}

// --- Inkrementální umístění (E7-US3/US3b) — bez ELK, lehký lokální výpočet. ---

const RADII = [280, 460, 640, 820];
const DIRECTIONS = 8;
/** Váha "nová vazba kříží jinou vazbu" vůči "vzdálenost od propojených tabulek"
 * (epic-7 kap. 9 — přesné váhy jsou otevřená otázka, dolaďuje se implementací). */
const CROSSING_PENALTY = 400;

function candidateOffsetsAtRadius(radius: number): Point[] {
  const offsets: Point[] = [];
  for (let i = 0; i < DIRECTIONS; i += 1) {
    const angle = (i / DIRECTIONS) * 2 * Math.PI;
    offsets.push({ x: Math.round(Math.cos(angle) * radius), y: Math.round(Math.sin(angle) * radius) });
  }
  return offsets;
}

function rectsOverlap(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

function overlapsAnyTable(
  candidate: Point,
  size: BoxSize,
  effectivePositions: Record<string, Point>,
  tableSizes: Record<string, BoxSize>,
  excludeTableId: string,
): boolean {
  const candidateRect = { x: candidate.x, y: candidate.y, width: size.width, height: size.height };
  for (const [tableId, position] of Object.entries(effectivePositions)) {
    if (tableId === excludeTableId) continue;
    const otherSize = tableSizes[tableId] ?? { width: TABLE_NODE_WIDTH, height: TABLE_NODE_FALLBACK_HEIGHT };
    if (rectsOverlap(candidateRect, { x: position.x, y: position.y, width: otherSize.width, height: otherSize.height })) {
      return true;
    }
  }
  return false;
}

/** Standardní orientation-based test průsečíku dvou úseček. */
function segmentsIntersect(p1: Point, p2: Point, p3: Point, p4: Point): boolean {
  const orientation = (a: Point, b: Point, c: Point): number => {
    const value = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
    if (value === 0) return 0;
    return value > 0 ? 1 : 2;
  };
  const onSegment = (a: Point, b: Point, c: Point): boolean =>
    b.x <= Math.max(a.x, c.x) && b.x >= Math.min(a.x, c.x) && b.y <= Math.max(a.y, c.y) && b.y >= Math.min(a.y, c.y);

  const o1 = orientation(p1, p2, p3);
  const o2 = orientation(p1, p2, p4);
  const o3 = orientation(p3, p4, p1);
  const o4 = orientation(p3, p4, p2);

  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(p1, p3, p2)) return true;
  if (o2 === 0 && onSegment(p1, p4, p2)) return true;
  if (o3 === 0 && onSegment(p3, p1, p4)) return true;
  if (o4 === 0 && onSegment(p3, p2, p4)) return true;
  return false;
}

function countCrossings(candidate: Point, connectedPositions: Point[], existingSegments: { from: Point; to: Point }[]): number {
  let count = 0;
  for (const connected of connectedPositions) {
    for (const segment of existingSegments) {
      if (segmentsIntersect(candidate, connected, segment.from, segment.to)) count += 1;
    }
  }
  return count;
}

function distanceSum(candidate: Point, connectedPositions: Point[]): number {
  return connectedPositions.reduce((sum, position) => sum + Math.hypot(candidate.x - position.x, candidate.y - position.y), 0);
}

/**
 * Umístění jedné nové tabulky bez přeuspořádání zbytku schématu — kandidáti
 * v prstencích okolo těžiště propojených tabulek, ohodnocení kombinací
 * vzdálenosti a počtu nově vzniklých křížení s existujícími vazbami; vybere
 * se nejbližší prsten, ve kterém existuje aspoň jeden volný (nepřekrývající)
 * kandidát, a mezi jeho kandidáty ten s nejnižším skóre.
 */
export function computeIncrementalPlacement(
  schema: SchemaModel,
  effectivePositions: Record<string, Point>,
  tableSizes: Record<string, BoxSize>,
  newTableId: string,
  connectedTableIds: string[],
): Point {
  const newTableSize = tableSizes[newTableId] ?? { width: TABLE_NODE_WIDTH, height: TABLE_NODE_FALLBACK_HEIGHT };
  const connectedPositions = connectedTableIds
    .map((id) => effectivePositions[id])
    .filter((position): position is Point => Boolean(position));

  const centroid =
    connectedPositions.length > 0
      ? {
          x: connectedPositions.reduce((sum, p) => sum + p.x, 0) / connectedPositions.length,
          y: connectedPositions.reduce((sum, p) => sum + p.y, 0) / connectedPositions.length,
        }
      : (effectivePositions[newTableId] ?? { x: 0, y: 0 });

  const existingSegments = resolveForeignKeyEdges(schema)
    .filter((edge) => edge.targetTableId && edge.sourceTableId !== edge.targetTableId)
    .map((edge) => ({
      from: effectivePositions[edge.sourceTableId],
      to: effectivePositions[edge.targetTableId as string],
    }))
    .filter((segment): segment is { from: Point; to: Point } => Boolean(segment.from) && Boolean(segment.to));

  for (const radius of RADII) {
    let best: { position: Point; score: number } | null = null;
    for (const offset of candidateOffsetsAtRadius(radius)) {
      const candidate = { x: centroid.x + offset.x, y: centroid.y + offset.y };
      if (overlapsAnyTable(candidate, newTableSize, effectivePositions, tableSizes, newTableId)) continue;
      const score = distanceSum(candidate, connectedPositions) + CROSSING_PENALTY * countCrossings(candidate, connectedPositions, existingSegments);
      if (!best || score < best.score) best = { position: candidate, score };
    }
    if (best) return best.position;
  }

  // Všichni kandidáti na všech vyzkoušených poloměrech se s něčím překrývali
  // (velmi husté schéma) — umísti aspoň mimo nejvzdálenější vyzkoušený prsten.
  const fallbackRadius = RADII[RADII.length - 1] + 180;
  return { x: centroid.x + fallbackRadius, y: centroid.y };
}

/**
 * E7-US3b — sdíleno mezi oběma vstupními body vytváření FK (`CanvasView.tsx`
 * `handleConnectEnd`, `workspace/Inspector.tsx` `ForeignKeysSection`), volané
 * PŘED přidáním nové FK do schématu (`isTableConnected` se ptá na stav před
 * vazbou). Vrací umístění pro každou stranu nové vazby, která dosud neměla
 * ŽÁDNOU FK (0, 1, nebo 2 položky — self-referenční FK nikdy nevrací nic,
 * spojuje tabulku samu se sebou, není co prostorově přiblížit).
 */
export function computePlacementsForNewConnection(
  schema: SchemaModel,
  effectivePositions: Record<string, Point>,
  tableSizes: Record<string, BoxSize>,
  referencingTableId: string,
  referencedTableId: string,
): { tableId: string; position: Point }[] {
  if (referencingTableId === referencedTableId) return [];
  const placements: { tableId: string; position: Point }[] = [];
  if (!isTableConnected(schema, referencingTableId)) {
    placements.push({
      tableId: referencingTableId,
      position: computeIncrementalPlacement(schema, effectivePositions, tableSizes, referencingTableId, [referencedTableId]),
    });
  }
  if (!isTableConnected(schema, referencedTableId)) {
    placements.push({
      tableId: referencedTableId,
      position: computeIncrementalPlacement(schema, effectivePositions, tableSizes, referencedTableId, [referencingTableId]),
    });
  }
  return placements;
}
